import React, { useContext, useEffect, useRef, useState } from "react";
import { missionURL } from "../../Hooks/URL";
import useFetch from "../../Hooks/useFetch";
import { useParams } from "react-router-dom";
import { userContext } from "../../Contexts/userContext";
import Mission from "../../Template/Home/Mission/Mission";
import useFetchWhen from "../../Hooks/useFetchWhen";
import useSave from "../../Hooks/useSave";
import axios from "axios";
import { toast } from "react-toastify";
import {
  loadAnswersFromStorage,
  saveAnswerToStorage,
  clearStorage,
  getUserId,
} from "../../Template/Home/Mission/Utils/mcqStorage";

function MissionPage() {
  const { contextData, setContextData } = useContext(userContext);
  const { id } = useParams();

  const [mission, setMission] = useState({});
  const [missionLoading, setMissionLoading] = useState(false);

  const [timeLeftToStart, setTimeLeftToStart] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);

  // Upload section state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef();

  // MCQ section state
  const [mcqAnswers, setMcqAnswers] = useState({});
  const [mcqSubmitting, setMcqSubmitting] = useState(false);
  const [mcqSubmitStatus, setMcqSubmitStatus] = useState("");
  const [mcqReviewData, setMcqReviewData] = useState(null);

  const { data, loading, error } = useFetch(`${missionURL}/${id}`);

  useEffect(() => {
    if (!loading && !error && data) {
      setMission(data.data);
    }
  }, [data, loading, error]);

  // Clear MCQ status when mission/route changes
  useEffect(() => {
    setMcqSubmitStatus("");
    setMcqReviewData(null);
    setMcqSubmitting(false);
  }, [id]);

  // Load answers from localStorage on mount (only if mission is MCQ and not submitted)
  useEffect(() => {
    if (
      id &&
      mission &&
      mission?.task?.type === "mcq" &&
      !mission?.submission
    ) {
      const userId = getUserId(contextData);
      if (userId) {
        const savedAnswers = loadAnswersFromStorage(userId, id);
        if (Object.keys(savedAnswers).length > 0) {
          // Replace existing answers with saved ones (localStorage takes priority)
          setMcqAnswers(savedAnswers);
        }
      }
    }
  }, [id, contextData, mission]);

  useEffect(() => {
    if (mission) {
      // Use server's pre-calculated time values
      if (mission?.missionSubmissionStatus?.timing) {
        const timeToStartInSeconds =
          mission?.missionSubmissionStatus?.timing?.timeToStartInSeconds;
        const timeToEndInSeconds =
          mission?.missionSubmissionStatus?.timing?.timeToEndInSeconds;

        // Convert seconds to milliseconds for consistency
        const timeToStartMs = timeToStartInSeconds * 1000;
        const timeToEndMs = timeToEndInSeconds * 1000;

        setTimeLeftToStart(timeToStartMs);
        setTimeLeft(timeToEndMs);
      }
    }
  }, [mission]);

  const refetchMission = async (loading = true) => {
    try {
      if (loading) {
        setMissionLoading(true);
      }
      const response = await axios.get(`${missionURL}/${id}`, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
      if (response.data && response.data.data) {
        setMission(response.data.data);
        if (loading) {
          setMissionLoading(false);
        }
      }
    } catch (error) {
      console.error("Error refetching mission:", error);
      if (loading) {
        setMissionLoading(false);
      }
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeftToStart((prevTime) => {
        const newTime = Math.max(0, prevTime - 1000);
        return newTime;
      });

      setTimeLeft((prevTime) => {
        const newTime = Math.max(0, prevTime - 1000);
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (timeLeftToStart <= 0 && !mission?.task && mission?.id) {
      refetchMission();
    }
  }, [timeLeftToStart]);

  useEffect(() => {
    if (timeLeft <= 0 && mission?.id) {
      refetchMission();
    }
  }, [timeLeft]);

  // Auto-refetch mission data every 1 minute
  useEffect(() => {
    const timeInterval = setInterval(() => {
      refetchMission(false);
    }, 60000 * 5); // 60 sec
    return () => clearInterval(timeInterval);
  }, []); // Remove refetchMission dependency to prevent infinite re-renders

  useEffect(() => {
    if (mission?.missionSubmissionStatus?.timing) {
      const timeToStartInSeconds =
        mission?.missionSubmissionStatus?.timing?.timeToStartInSeconds;
      const timeToEndInSeconds =
        mission?.missionSubmissionStatus?.timing?.timeToEndInSeconds;

      // Convert seconds to milliseconds for consistency
      const timeToStartMs = timeToStartInSeconds * 1000;
      const timeToEndMs = timeToEndInSeconds * 1000;

      setTimeLeftToStart(timeToStartMs);
      setTimeLeft(timeToEndMs);
    }
  }, [mission?.missionSubmissionStatus?.timing]);

  // MCQ answer handling
  const handleMcqAnswerChange = (questionIndex, answer) => {
    setMcqAnswers((prev) => {
      // Normalize questionIndex to handle both string and number keys
      const key = String(questionIndex);
      const numKey = Number(questionIndex);
      const updated = {
        ...prev,
        [key]: answer,
        // Also save with numeric key for compatibility (if it's a valid number)
        ...(isNaN(numKey) ? {} : { [numKey]: answer }),
      };

      // Save to localStorage (async, non-blocking)
      if (id && contextData?.user) {
        const userId = getUserId(contextData);
        if (userId) {
          // Save by questionIndex (as string, which is the primary key)
          saveAnswerToStorage(userId, id, key, answer);
          // Also save as number if it's a valid number
          if (!isNaN(numKey)) {
            saveAnswerToStorage(userId, id, numKey, answer);
          }
        } else {
          console.warn(
            "handleMcqAnswerChange - userId is null, cannot save to localStorage"
          );
        }
      } else {
        console.warn("handleMcqAnswerChange - Missing id or contextData.user", {
          id,
          hasUser: !!contextData?.user,
        });
      }

      return updated;
    });
  };

  // Build standardized MCQ payload
  const buildMcqPayload = () => {
    const questionsList =
      mission?.task?.missionTaskMCQ?.questions ||
      Array.from(
        { length: mission?.task?.numberOfQuestions || 0 },
        (_, index) => ({ id: index })
      );

    const mcqAnswersArray = questionsList.map((q, qIndex) => {
      const questionId = q?.id ?? qIndex;

      // Try to get answer by question ID first, then by index (backward compatibility)
      const selectedSymbol =
        mcqAnswers[questionId] ||
        mcqAnswers[qIndex] ||
        mcqAnswers[String(questionId)] ||
        mcqAnswers[String(qIndex)] ||
        "";

      let answerId = null;

      if (selectedSymbol && q?.answers) {
        const matchedAnswer = q.answers.find((answer, aIndex) => {
          const symbol = answer.symbol || String.fromCharCode(65 + aIndex);
          return symbol === selectedSymbol;
        });

        if (matchedAnswer) {
          const fallbackIndex = q.answers.indexOf(matchedAnswer);
          answerId =
            typeof matchedAnswer.id !== "undefined"
              ? matchedAnswer.id
              : fallbackIndex;
        }
      }

      return {
        questionId,
        answerId,
      };
    });

    return {
      submissionFile: "",
      missionTaskType: "mcq",
      mcqAnswers: mcqAnswersArray,
    };
  };

  // MCQ review API (called when user clicks "Review & Submit Answers")
  const handleMcqReview = async () => {
    const payload = buildMcqPayload();

    setMcqSubmitting(true);
    setMcqSubmitStatus("Preparing review...");

    try {
      const response = await axios.post(
        `${missionURL}/${id}/mcq/review`,
        payload,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (response.data.status === "success") {
        setMcqSubmitStatus("✅ Review data prepared successfully.");
        // Store the review response data
        const reviewData = response.data.data || response.data;
        setMcqReviewData(reviewData);
        // Always return a truthy value on success (use reviewData or a success object)
        return reviewData || { success: true, ...response.data };
      }

      throw new Error(response.data.message || "Review request failed");
    } catch (error) {
      console.error("MCQ review error:", error);

      if (error.response?.data?.message) {
        setMcqSubmitStatus(`❌ Review failed: ${error.response.data.message}`);
        toast.error(error.response.data.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnFocusLoss: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      } else {
        setMcqSubmitStatus("❌ Review failed. Please try again.");
        toast.error("Review failed. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnFocusLoss: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      }

      return false;
    } finally {
      setMcqSubmitting(false);
    }
  };

  // MCQ final submit API (called when user confirms in review modal)
  const handleMcqSubmit = async () => {
    const payload = buildMcqPayload();

    // Allow submission even if not all questions are answered
    setMcqSubmitting(true);
    setMcqSubmitStatus("Submitting answers...");

    try {
      const response = await axios.post(`${missionURL}/${id}/submit`, payload, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (response.data.status === "success") {
        setMcqSubmitStatus("✅ Answers submitted successfully!");

        // Clear localStorage after successful submission
        if (id && contextData?.user) {
          const userId = getUserId(contextData);
          if (userId) {
            clearStorage(userId, id);
          }
        }

        toast.success("MCQ answers submitted successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnFocusLoss: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });

        if (refetchMission) {
          refetchMission();
        }
        // Clear status after refetch
        setMcqSubmitStatus("");
      } else {
        throw new Error(response.data.message || "Submission failed");
      }
    } catch (error) {
      console.error("MCQ submission error:", error);

      if (error.response?.data?.message) {
        setMcqSubmitStatus(
          `❌ Submission failed: ${error.response.data.message}`
        );
        toast.error(error.response.data.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnFocusLoss: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      } else {
        setMcqSubmitStatus("❌ Submission failed. Please try again.");
        toast.error("Submission failed. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnFocusLoss: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      }
    } finally {
      setMcqSubmitting(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) {
      setUploadStatus("Please select a file to upload.");
      return;
    }

    // Check if file is one of the allowed types
    const allowedExtensions = [".zip", ".rar", ".py", ".sb3"];
    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf("."));

    if (!allowedExtensions.includes(fileExtension)) {
      setUploadStatus(
        "Only ZIP, RAR, Python (.py), and Scratch (.sb3) files are allowed."
      );
      return;
    }
    // Check file size (15MB = 15 * 1024 * 1024 bytes) - reduced to prevent 413 errors
    const maxSize = 15 * 1024 * 1024; // 15MB in bytes
    if (file.size > maxSize) {
      setUploadStatus(
        "❌ File size exceeds 15MB limit. Please compress your file or choose a smaller one."
      );
      return;
    }

    setUploading(true);
    setUploadStatus("Preparing upload...");
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append("submissionFile", file);
      formData.append("missionTaskType", "mission");

      const response = await axios.post(
        `${missionURL}/${id}/submit`,
        formData,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      if (response.data.status === "success") {
        setUploadStatus(
          "✅ Upload successful! Your solution has been submitted."
        );
        setUploadProgress(100);
        toast.success("File uploaded successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnFocusLoss: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
        // Refresh the page after successful upload
        setTimeout(() => {
          if (refetchMission) {
            refetchMission();
          }
        }, 2000);
      } else {
        throw new Error(response.data.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);

      if (error.response?.status === 413) {
        setUploadStatus(
          "❌ File too large! Please compress your file or choose a smaller file (max 15MB)."
        );
        toast.error("File too large! Please compress your file.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnFocusLoss: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      } else if (error.response?.data?.message) {
        setUploadStatus(`❌ Upload failed: ${error.response.data.message}`);
        toast.error(error.response.data.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnFocusLoss: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      } else {
        setUploadStatus(
          "❌ Upload failed. Please check your connection and try again."
        );
        toast.error("Upload failed. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnFocusLoss: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      }
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Mission
      id={id}
      contextData={contextData}
      mission={mission}
      timeLeftToStart={timeLeftToStart}
      timeLeft={timeLeft}
      loading={loading || missionLoading}
      handleMcqAnswerChange={handleMcqAnswerChange}
      handleMcqReview={handleMcqReview}
      handleMcqSubmit={handleMcqSubmit}
      handleUpload={handleUpload}
      mcqAnswers={mcqAnswers}
      mcqSubmitting={mcqSubmitting}
      mcqSubmitStatus={mcqSubmitStatus}
      mcqReviewData={mcqReviewData}
      selectedFile={selectedFile}
      uploadStatus={uploadStatus}
      uploading={uploading}
      uploadProgress={uploadProgress}
      fileInputRef={fileInputRef}
      setSelectedFile={setSelectedFile}
      setUploadStatus={setUploadStatus}
      setUploadProgress={setUploadProgress}
    />
  );
}

export default MissionPage;
